// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit
// ─────────────────────────────────────────────────────────────────────────────
// R&D BOARD (route /board, nav label "R&D").
//
// CONSOLIDATED 2026-08-02 ON EREZ'S EXPLICIT INSTRUCTION: "the R&D board is a
// mess… consolidate into two diagrams and two knowledge base pages, and delete
// what we don't need." The cumulative-board law (root CLAUDE.md, "WHAT A BOARD
// IS MADE OF") says nothing comes off without his word. He gave it.
//
// ── WHAT CAME OFF, AND WHERE IT WENT — nothing was destroyed ────────────────
// Every removed section is still on disk and still in git; the KB pages are all
// still in the knowledge base at /knowledge-base. Only the BOARD is smaller.
//
//   rnd/ServerArchitecture.tsx   the big client-server diagram  → unmounted, on disk
//   command/falcon-omnigent.tsx  deployment + data-flow views   → unmounted, on disk
//                                (container view SURVIVES as FalconSystemDiagram)
//   command/engine-map.tsx       the engine map                 → unmounted, on disk
//   rnd/OmnigentArch.tsx         omnigent architecture views    → unmounted, on disk
//   command/context-strategy.tsx two-stores + one-turn + reset  → unmounted, on disk
//                                (memory loop SURVIVES as ContextMemoryDiagram)
//
//   kb-doc/myjarvis-os/omnigent/{overview,decision,head-to-head,architecture,
//     evidence,conversion-plan,from-the-source,the-answer,every-candidate,
//     the-sweep}  → ALL still live in the KB, just no longer presented here.
//     That is the 160-project sweep and the omnigent decision record; it is
//     research, it is not deleted, and deleting it needs its own instruction.
//
// ── CONSOLIDATED AGAIN 2026-08-03, ON EREZ'S WORD ──────────────────────────
// "We have three sections and each one of them has Falcon stuff. Consolidate
// everything into one section." So the three FALCON — * boxes are now ONE box.
// NOTHING CAME OFF: both diagrams and all four documents are still mounted,
// they just live inside a single gray box in two rows. A fifth document — the
// Telegram channel record — is added below them.
//
// ── ADDED 2026-08-09, ON EREZ'S INSTRUCTION: THE PLATFORM ──────────────────
// "Bring this knowledge base page to the R&D board and just display it so I can
// see it." NOTHING came off. This is a new box, placed FIRST because it is the
// baseline that the client sections below are instances OF.
//
// Until today this board had exactly two sections and both were clients, which
// was the whole complaint: we have built the same system three times and the
// general knowledge kept being filed inside a client folder. The STANDARD page
// has existed since 7 August and nothing on any board or in any agent contract
// pointed at it. Now the board opens with it.
//
//   kb-doc/myjarvis-os/architecture/tailormind-core     the CORE
//   kb-doc/myjarvis-os/architecture/tailormind-falcon   the FIRST INSTANCE
//
// -- ADDED 2026-08-10: THE SECOND INSTANCE ---------------------------------
//   kb-doc/myjarvis-os/architecture/tailormind-outform  the SECOND INSTANCE
//
// -- ADDED 2026-08-22: THE FIFTH, AND THE FIRST ONE THAT IS OURS -----------
//   kb-doc/myjarvis-os/architecture/tailormind-erez     THE CEO SHAPE, BUILT
//
// The CEO document has sat in this row since 16 Aug describing a grid of live
// agent conversations and an agent-owned window, labelled NOT BUILT YET. Both
// exist now, on the box we run ourselves on, so the plan and the instance sit
// side by side and the label on the plan changes to THE PLAN.
//
// It earns the space the same way every other instance does -- by diverging.
// It is the only one whose operator is us, the only one serving its dashboard
// from a live dev server, and the only one where an agent edits a surface and
// the person watching sees it change with no build. Three client instances can
// all agree with the core and still never show what the core is FOR.
//
// Nothing came off. The PLATFORM row was a pair -- the system before a client
// and the system as adjusted for one -- and a pair cannot show which of its
// differences are the CLIENT'S and which are OURS. A second instance is what
// turns a divergence into evidence: where Falcon and Outform diverge the same
// way, that is the core being wrong and the divergence should be promoted;
// where they diverge oppositely (RTL vs LTR, messaging-first vs console-first),
// that is the client showing through and the core should stop assuming either.
// Outform is also the first instance built BEFORE a contract, from the
// prospect's own blueprint, which is the other reason it belongs up here.
//
// TWO, NOT FOUR (Erez, 2026-08-09). The section opened with four documents that
// mixed altitudes: a standard, a file-by-file diff that was evidence for it, and
// two client instances. Erez cut it to the pair that carries the idea — the
// system before a client, and the system as adjusted for one. Everything else
// is an instance of the first and belongs on its own project's board.
//
// The four pages are NOT deleted. Two of them (Air Duct, Everything 420) are
// Hank's and Ruby's, presented on their boards; removing them here removes them
// from THIS board only.
//
// ── WHAT THE BOARD IS NOW: three sections ──────────────────────────────────
//   THE PLATFORM  ─  the standard, its evidence, and the instances that exist
//   FALCON        ├─ row 1  the platform diagram + the two loops diagram
//                 └─ row 2  five KB pages, presented via <DocumentFrame>
//   BOILING OFFERS
//
// One SectionGrid, one node. Nothing hand-placed, so nothing can overlap.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from "react";
import { type Node, type NodeProps, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { FalconPlatformDiagram, FalconLoopsDiagram } from "./command/falcon-platform";
import { ContextRollDiagram, ContextFaultsDiagram } from "./command/context-roll";
import { BoilingPlatformDiagram } from "./command/boiling-platform";
import { TailormindEstateDiagram } from "./command/tailormind-estate";
import { DataProgrammeDiagram } from "./command/data-programme";
import { Outform264714LayersDiagram } from "./command/outform-264714-layers";
import { OutformStagingViewer } from "./command/outform-staging";
import { DocumentFrame } from "./DocumentFrame";
import { TicketsKanbanPage } from "../tickets/TicketsKanbanPage";
import { OutformConceptThree } from "./atlas/outform-concept3";
import { OutformConceptFour } from "./atlas/outform-concept4";
import { OutformConceptFive } from "./atlas/outform-concept5";

/**
 * Board width. Two diagram sections wrap alongside two 900px document frames.
 * MEASURE in the live DOM before changing this — a paper calculation was 384px
 * short once already, because a card rendered wider than the width it declared.
 */
const GRID_W = 14000;

const DOC_W = 900;
const DOC_H = 1180;

/** Force a row break so a wide section gets the row to itself. */
const Break = () => <div style={{ flexBasis: "100%", height: 0 }} />;

function SectionsNode(_props: NodeProps) {
  return (
    <SectionGrid width={GRID_W}>
      {/* ── ACTION ITEMS (Erez, 2026-08-16) ──────────────────────────────────
          FIRST, and above the platform documents, because it is the only
          section that answers "what are we doing next". Everything below is
          reference — the standard, the instances, the diagrams. This is the
          one you act on.

          It is the same /tickets Kanban Falcon embeds, not an R&D component:
          <TicketsKanbanPage project="…" embedded /> is the ONE app, sliced by
          project slug. Ours is `rnd`. A card dragged here moves everywhere,
          because it persists through the same PUT the full page uses. */}
      <SectionBox title="Action items" layout="custom">
        <div style={{ marginTop: 0 }}>
          <TicketsKanbanPage project="rnd" embedded />
        </div>
      </SectionBox>
      <Break />
      {/* ── AGENT-AUTHORED VIEWS (Erez, 2026-08-29) ─────────────────────────
          Immediately under Action items because it is a DECISION waiting on
          him, not reference. Erez: "it's an interesting architecture… put it
          on your board… I want us to just think one more minute about our
          architecture and just validate it."

          The proposal, in one line: ONE normalized database, and a front end
          written PER JOB from a managed template, running in a sandbox — the
          same pattern as Anthropic's Artifacts and our own Jarvis View. It is
          marked PROPOSED and section 09 of the page is the honest list of what
          is still unsolved (print/export, approval, payload versioning).

          Presented, not re-typed: the writing lives in page_content and is
          edited THERE. It carries no FlowDiagram, so DocumentFrame is safe —
          a KB page with a diagram inside nests a ReactFlow in the board's own
          ReactFlow and blanks the canvas (skill://board). */}
      <SectionBox title="AGENT-AUTHORED VIEWS — proposed, and waiting on your word" layout="custom">
        <DocumentFrame slug="kb-doc/myjarvis-os/architecture/agent-authored-views"
          label="One normalized database, a front end per job" width={DOC_W} height={DOC_H} />
      </SectionBox>
      <Break />
      {/* ── 264714, TAB BY TAB (Erez, 2026-08-29 evening) ────────────────────
          Directly under Action items because it is the section that answers
          "what am I actually looking at" — and until you can answer that,
          nothing else on this board is readable.

          Erez: "I want to see all the tabs that they have, and how are they
          mapped into our database, and where is the logic and everything.
          Because I don't understand what you're showing me." He had spent an
          evening expecting Outform's Executive Summary in the product and
          getting a reconciliation table instead — which proves the engine is
          right and is a different artefact entirely.

          ⛔ IT IS A DIAGRAM SECTION, NOT A PRESENTED DOCUMENT, AND THAT IS THE
          POINT. The same content presented on Quinn's board via <DocumentFrame>
          blanked it an hour ago: the KB page carries a FlowDiagram, so showing
          it nested a ReactFlow inside the board canvas's own ReactFlow. Jarvis
          confirmed the mechanism in source and wrote the prohibition into
          skill://board. A diagram is a legal board section in its own right —
          it never needed to arrive wrapped in a document. Law: skill://diagrams.

          The written version, with the per-sheet table, stays in the KB at
          /kb-doc/consultancy/outform/the-workbook-mapped and is LINKED, not
          presented. */}
      <SectionBox title="264714 — every tab, and the layer it became" layout="custom">
        <Outform264714LayersDiagram />
      </SectionBox>
      <Break />
      {/* ── THE DATA PROGRAMME (Erez, 2026-08-28) ────────────────────────────
          Directly under Action items because it is the only section that
          answers "what is the process" rather than "what is the state". Erez
          asked for it here by name.

          A data-flow diagram on the house engine — the numbered discs ARE the
          diagram. Written generic on purpose: every client with a spreadsheet
          estate runs these six stages, and Outform is simply the first one we
          have measured. Law: skill://diagrams. */}
      <SectionBox title="THE DATA PROGRAMME — how a spreadsheet estate becomes a queryable history" layout="custom">
        <DataProgrammeDiagram />
      </SectionBox>
      <Break />
      {/* ── OUTFORM STAGING · LIVE (MJOS-219, 2026-08-28) ────────────────────
          The window onto the database as it fills. Erez asked "how are we
          actually seeing it happening?" and the honest answer was that he
          could not — the viewer sat behind fifty more files on my own
          checklist. It moved ahead of file two.

          Reads /api/outform-staging, which is READ ONLY and points at OUR
          staging schema, never Outform's live database. */}
      <SectionBox title="OUTFORM STAGING — the database, live, as it fills" layout="custom">
        <OutformStagingViewer />
      </SectionBox>
      <Break />
      {/* ── THE PLATFORM (Erez, 2026-08-09) ──────────────────────────────────
          The baseline, first, because everything below it is an instance of it.
          Per the board law these are KB pages PRESENTED here — the writing lives
          in page_content and is edited THERE, never in this file.

          Left to right the row is an argument: here is the standard · here is
          the measurement that earns it the word "standard" rather than an
          assertion · and here are the two systems that have actually written
          down where they diverge from it. The two that have not are missing on
          purpose. */}
      <SectionBox title="THE PLATFORM" layout="custom">
        <div style={{ display: "flex", gap: 26 }}>
          <DocumentFrame slug="kb-doc/myjarvis-os/architecture/tailormind-core"
            label="The system before it meets a client" width={DOC_W} height={DOC_H} />
          <DocumentFrame slug="kb-doc/myjarvis-os/architecture/tailormind-falcon"
            label="The first instance, and where it diverges" width={DOC_W} height={DOC_H} />
          <DocumentFrame slug="kb-doc/myjarvis-os/architecture/tailormind-outform"
            label="The second instance, built before a contract" width={DOC_W} height={DOC_H} />
          {/* The third instance, added 13 Aug 2026. It existed as a running system
              and two repositories for weeks with no document at all — which is
              why "the three are aligned" could not be true. Its value here is the
              divergences: the only client not on Hetzner, the only one still on
              a floating `:latest` image, and the only one whose console lives in
              two places. A row of three instances is what turns a difference into
              evidence about the core. */}
          <DocumentFrame slug="kb-doc/myjarvis-os/architecture/tailormind-boiling"
            label="The third instance, and the one that diverges most" width={DOC_W} height={DOC_H} />
          {/* The fourth, added 16 Aug 2026 — and it belongs in THIS row, which I
              got wrong for one commit. I gave it a box of its own on the theory
              that it was a "product shape" rather than a client. Erez: "it's just
              another Tailormind app, and we're going to have many of them, on the
              same core." He is right, and the tell is Falcon: it runs a voice
              service and a Telegram bridge that Outform does not, and nobody
              called that a new category. A divergence is what an instance IS.
              The grid and the agent-owned window are this one's divergences.

              The only real difference is status, not kind: the other three are
              running systems and this is a plan. That belongs in the label. */}
          <DocumentFrame slug="kb-doc/myjarvis-os/architecture/tailormind-ceo"
            label="The fourth — the grid and the agent's own app · THE PLAN" width={DOC_W} height={DOC_H} />
          {/* The fifth, added 22 Aug 2026, and it belongs beside the CEO plan
              rather than at the end: it IS that shape, running. The grid of live
              agent conversations and the agent-owned window were the two things
              the CEO document described and nothing had built; both are on this
              box now, each agent carrying its own page and its own next steps.

              Its value in this row is the same as every other instance — the
              divergences. It is the only one whose operator is us, the only one
              serving its dashboard from a live dev server, and the only one
              where an agent edits a surface and the person watching sees it
              change without a build. That last property is why the row needed
              it: three client instances can agree with the core and still not
              show what the core is for. */}
          <DocumentFrame slug="kb-doc/myjarvis-os/architecture/tailormind-erez"
            label="The fifth — the CEO shape actually running, and ours" width={DOC_W} height={DOC_H} />
        </div>
      </SectionBox>

      {/* ── ROW BREAK — THE ESTATE gets its own line ─────────────────────────
          It is a wide deployment view; sharing a row would squeeze it. */}
      <div style={{ width: "100%", height: 0 }} />

      {/* ── THE ESTATE (Erez, 2026-08-12) ────────────────────────────────────
          The view ACROSS clients, which this board did not have: every machine
          we run and the repository each one came from. THE PLATFORM above says
          what a Tailormind system IS; this says what we have actually built,
          four times, and where the four disagree.

          Read off the live machines rather than from memory. The finding is in
          the wires that are ABSENT: no client's front end traces back to a
          repository, and Air Duct traces back to nothing at all. */}
      <SectionBox title="THE ESTATE" layout="custom">
        <TailormindEstateDiagram />
      </SectionBox>

      {/* ── ROW BREAK (Erez, 2026-08-09: "put the platform section above the
          other sections") ───────────────────────────────────────────────────
          SectionGrid is a flex-wrap row, so THE PLATFORM would otherwise sit
          shoulder-to-shoulder with FALCON and read as a peer. A zero-height
          100%-wide child consumes the rest of the line and pushes everything
          after it onto the next row. That is the whole point: the baseline sits
          ABOVE the instances, not beside them. */}
      <div style={{ width: "100%", height: 0 }} />

      {/* ── ONE BOX FOR FALCON (Erez, 2026-08-03) ────────────────────────────
          It used to be three, which made three gray boxes for one project.
          Two rows inside one box: the pictures, then the writing. */}
      <SectionBox title="FALCON" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 140 }}>
          {/* ── row 1 · THE PICTURES ────────────────────────────────────────
              Left: what Falcon is made of, who touches it, and where OUR
              software ends and THEIRS begins — arranged around one answer,
              that the two systems meet at exactly one seam.
              Right: the two process flows that are genuinely ours rather than
              omnigent's — how a conversation hands itself over when it runs out
              of context, and how a write reaches an open screen with nobody
              pressing refresh. Numbered discs, because in a flow the ORDER is
              the content. */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 200 }}>
            <FalconPlatformDiagram />
            <FalconLoopsDiagram />
          </div>

          {/* ── row 1b · THE ROLL AT CODE ALTITUDE (added 2026-08-06) ───
              Loop A above draws the handover as an IDEA — five stages. Erez
              asked the next question down after the 6-August outage: what does
              the CODE do, in what order, in which process, and where did it do
              the wrong thing. Left: one round-trip end to end, with the branch
              tree inside `context_ceiling` and the label lookup everything
              downstream now depends on. Right: the two faults it replaces,
              drawn the same way so they compare directly — one shipped and cost
              fifteen minutes of Dan's morning, the other was caught by
              re-reading the code before testing it. */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 200 }}>
            <ContextRollDiagram />
            <ContextFaultsDiagram />
          </div>

          {/* ── row 2 · THE WRITING ─────────────────────────────────────────
              Per the board law these are KB pages PRESENTED here, not prose
              typed into this file. Each one lives under
              kb-doc/consultancy/falcon/ and is edited THERE, not here. */}
          <div style={{ display: "flex", gap: 26 }}>
            <DocumentFrame slug="kb-doc/consultancy/falcon/tailormind-standard"
              label="The standard every later app inherits" width={DOC_W} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/falcon/context-strategy"
              label="The context handover — as shipped" width={DOC_W} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/falcon/infrastructure"
              label="The box, as it is today" width={DOC_W} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/falcon/omnigent-architecture"
              label="The build plan it grew out of" width={DOC_W} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/falcon/telegram-formatting"
              label="Telegram — what the channel can render" width={DOC_W} height={DOC_H} />
          </div>
        </div>
      </SectionBox>

      {/* ── BOILING OFFERS ───────────────────────────────────────────────────
          The SECOND deployment of the standard, and the reason it is now a
          standard rather than an assertion: six platform modules are
          byte-identical to Falcon's, and `cmp` is a test anyone can run.
          Same two rows as FALCON on purpose — the picture, then the writing.
          Re-added 2026-08-04 after it went missing from this file; the diagram
          had survived on disk. */}
      <SectionBox title="BOILING OFFERS" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 140 }}>
          <BoilingPlatformDiagram />
          <div style={{ display: "flex", gap: 26 }}>
            <DocumentFrame slug="kb-doc/boiling-offers/rebuild-plan"
              label="The rebuild — decided and built, 3–4 August" width={DOC_W} height={DOC_H} />
            <DocumentFrame slug="kb-doc/myjarvis-os/evals"
              label="Evals — how we know a skill got better" width={DOC_W} height={DOC_H} />
            <DocumentFrame slug="kb-doc/boiling-offers/product-requirements"
              label="Lenny — what Matan asked for" width={DOC_W} height={DOC_H} />
          </div>
        </div>
      </SectionBox>

      {/* ── OUTFORM UX RESEARCH ──────────────────────────────────────────────
          Erez's design question for Outform: chat-primary with a document
          panel that splits in on generation (his Option A), or a persistent
          agent side panel next to a document that's always open (Option B)?
          20 real agentic products, ~130 surveyed, grouped by pattern —
          real screenshots, real sources, no invented examples. Added
          2026-08-23, Erez asked to see the screenshots on this board. */}
      <SectionBox title="OUTFORM UX RESEARCH" layout="custom">
        <DocumentFrame slug="kb-doc/research/outform-chat-document-ux-patterns"
          label="Chat + document patterns, 20 products — Option B recommended" width={DOC_W} height={DOC_H} />
      </SectionBox>

      {/* ── OUTFORM — CONCEPTS 3, 4, 5, updated 25 Aug 2026 ─────────────────
          Three, not one, per Erez's direction: keep chat docked on the side
          and the document always showing everything (that shape stays fixed
          across all three), but vary the MECHANISM of how the panel relates
          to the document — none of them an ordinary chat thread, and none of
          them Quinn's Story 1/2 (which IS a conversation). For comparison
          against Story 1/2 on /outform-board.
            Concept 3 · Review Queue   — numbered suggestion cards, accept/reject
            Concept 4 · Command Panel  — fixed action buttons, ephemeral results
            Concept 5 · Activity Feed  — ambient narrated log, occasional redirects
          Board clicks now work directly (SectionBox pointer-events fix,
          24 Aug) — the earlier standalone-page workaround is no longer
          needed for these, though OutformConceptThreePage.tsx is left as-is. */}
      <SectionBox title="OUTFORM — CONCEPT 3 · REVIEW QUEUE" layout="custom">
        <OutformConceptThree width={1710} />
      </SectionBox>

      <SectionBox title="OUTFORM — CONCEPT 4 · COMMAND PANEL" layout="custom">
        <OutformConceptFour width={1710} />
      </SectionBox>

      <SectionBox title="OUTFORM — CONCEPT 5 · ACTIVITY FEED" layout="custom">
        <OutformConceptFive width={1710} />
      </SectionBox>
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = {
  sections: SectionsNode as unknown as NodeTypes[string],
};

function buildNodes(): Node[] {
  return [
    {
      id: "sections",
      type: "sections",
      position: { x: 0, y: 0 },
      draggable: false,
      selectable: false,
      data: {},
    },
  ];
}

export function BoardPage() {
  const nodes = useMemo(() => buildNodes(), []);
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas
        nodes={nodes}
        edges={[]}
        nodeTypes={nodeTypes}
        fitViewPadding={0.06}
      />
    </div>
  );
}

export default BoardPage;
